// TourOverlay.js - Fixed version
import { useEffect, useState, useRef } from "react";
import {
  Backdrop,
  Box,
  Popper,
  Typography,
  Stack,
  Paper,
  Dialog,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import useTour from "./useTour";
import useGuidedTourNarration from "../guidedTour/useGuidedTourNarration";
import { isGuidedTourNarrationEnabled } from "../guidedTour/guidedTourFlags";
import GuidedTourStepFooter from "../guidedTour/GuidedTourStepFooter";
import {
  GUIDED_TOUR_CENTER_DIALOG_PAPER_SX,
  GUIDED_TOUR_CLOSE_BUTTON_SX,
  GUIDED_TOUR_POPOVER_CONTENT_SPACING,
  GUIDED_TOUR_POPOVER_PAPER_SX,
} from "../guidedTour/guidedTourPopoverStyles";

export default function TourOverlay() {
  const theme = useTheme();
  const isMobileWidth = useMediaQuery(theme.breakpoints.down("lg"));
  const isMobileHeight = useMediaQuery("(max-height: 500px)");
  const isMobileLayout = isMobileWidth || isMobileHeight;
  const narrationEnabled = isGuidedTourNarrationEnabled(isMobileLayout);

  const { speak, stop, isSpeaking } = useGuidedTourNarration();

  const {
    isOpen,
    currentStep,
    next,
    back,
    endTour,
    stepIndex,
    totalSteps,
  } = useTour();

  const [targetRect, setTargetRect] = useState(null);
  const [targetElement, setTargetElement] = useState(null);
  const popperRef = useRef(null);
  const remeasureTimerRef = useRef(null);
  const retryTimerRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      stop();
    }
  }, [isOpen, stop]);

  useEffect(() => {
    if (isOpen) {
      stop();
    }
  }, [stepIndex, isOpen, stop]);

  // Resolve target and get its position (viewport coordinates for fixed overlay)
  useEffect(() => {
    if (!isOpen || !currentStep) {
      setTargetRect(null);
      setTargetElement(null);
      return;
    }

    // Welcome-only: no spotlight target
    if (currentStep.selector === "body") {
      setTargetRect(null);
      setTargetElement(null);
      return;
    }

    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      const target = document.querySelector(currentStep.selector);

      if (!target) {
        // Skip missing targets after a short delay
        const skipTimer = setTimeout(() => {
          next();
        }, 100);
        return () => clearTimeout(skipTimer);
      }

      const applyViewportRect = (el) => {
        const r = el.getBoundingClientRect();
        setTargetRect({
          top: r.top,
          left: r.left,
          width: r.width,
          height: r.height,
        });
      };

      const scheduleRemeasure = (el) => {
        if (remeasureTimerRef.current) clearTimeout(remeasureTimerRef.current);
        remeasureTimerRef.current = setTimeout(() => {
          if (el && document.querySelector(currentStep.selector) === el) {
            applyViewportRect(el);
          }
          remeasureTimerRef.current = null;
        }, 400);
      };

      try {
        const rect = target.getBoundingClientRect();

        // Check if element is actually visible
        if (rect.width === 0 && rect.height === 0) {
          // Element might not be rendered yet, try again
          retryTimerRef.current = setTimeout(() => {
            const retryTarget = document.querySelector(currentStep.selector);
            if (retryTarget) {
              const retryRect = retryTarget.getBoundingClientRect();
              setTargetRect({
                top: retryRect.top,
                left: retryRect.left,
                width: retryRect.width,
                height: retryRect.height,
              });
              setTargetElement(retryTarget);
              retryTarget.scrollIntoView({ behavior: "smooth", block: "center" });
              scheduleRemeasure(retryTarget);
            } else {
              next();
            }
          }, 200);
          return () => {
            clearTimeout(timer);
            if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
            if (remeasureTimerRef.current) clearTimeout(remeasureTimerRef.current);
          };
        }

        // Viewport coordinates (no scroll) so spotlight aligns with fixed Backdrop/Box
        setTargetRect({
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
        });
        setTargetElement(target);

        // Scroll target into view, then re-measure so spotlight moves with element
        target.scrollIntoView({ behavior: "smooth", block: "center" });
        scheduleRemeasure(target);
      } catch (error) {
        console.error("Error getting target position:", error);
        next();
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      if (remeasureTimerRef.current) clearTimeout(remeasureTimerRef.current);
    };
  }, [isOpen, currentStep, next]);

  const tourFooterProps = {
    stepIndex,
    totalSteps,
    currentStep,
    onBack: back,
    onNext: next,
    narrationEnabled,
    speak,
    stop,
    isSpeaking,
  };

  // Keep hook order stable: block rendering on mobile after hooks initialize.
  if (isMobileLayout) return null;

  const isBodyWelcomeStep = currentStep?.selector === "body";
  const isCenterPopoverStep =
    currentStep?.placement === "center" && !isBodyWelcomeStep;

  const renderTourStepContent = (nextLabel) => (
    <>
      <IconButton
        aria-label="close guided tour"
        onClick={endTour}
        size="small"
        sx={GUIDED_TOUR_CLOSE_BUTTON_SX}
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      <Stack spacing={GUIDED_TOUR_POPOVER_CONTENT_SPACING}>
        {currentStep.title && (
          <Typography variant="subtitle2" fontWeight={700} sx={{ pr: 3 }}>
            {currentStep.title}
          </Typography>
        )}

        <Typography variant="body2" color="text.secondary">
          {currentStep.content}
        </Typography>

        <GuidedTourStepFooter
          {...tourFooterProps}
          nextLabel={nextLabel}
        />
      </Stack>
    </>
  );

  const renderCenterDialog = ({ hideBackdrop = false } = {}) => (
    <Dialog
      open
      onClose={endTour}
      hideBackdrop={hideBackdrop}
      maxWidth={false}
      sx={{ zIndex: 1400 }}
      PaperProps={{
        sx: GUIDED_TOUR_CENTER_DIALOG_PAPER_SX,
      }}
    >
      {renderTourStepContent(
        stepIndex === totalSteps - 1
          ? "Finish"
          : stepIndex === 0
            ? "Get Started"
            : "Next"
      )}
    </Dialog>
  );

  const renderSpotlightBackdrop = () => (
    <>
      <Backdrop
        open
        sx={{
          zIndex: 1300,
          backgroundColor: "rgba(0, 0, 0, 0.7)",
          clipPath: targetRect
            ? `polygon(
                0% 0%, 
                0% 100%, 
                ${targetRect.left}px 100%, 
                ${targetRect.left}px ${targetRect.top}px, 
                ${targetRect.left + targetRect.width}px ${targetRect.top}px, 
                ${targetRect.left + targetRect.width}px ${targetRect.top + targetRect.height}px, 
                ${targetRect.left}px ${targetRect.top + targetRect.height}px, 
                ${targetRect.left}px 100%, 
                100% 100%, 
                100% 0%
              )`
            : "none",
        }}
      />
      <Box
        sx={{
          position: "fixed",
          top: targetRect.top - 4,
          left: targetRect.left - 4,
          width: targetRect.width + 8,
          height: targetRect.height + 8,
          border: "3px solid",
          borderColor: "primary.main",
          borderRadius: 1,
          boxShadow: "0 0 0 9999px rgba(0, 0, 0, 0.3)",
          zIndex: 1301,
          pointerEvents: "none",
        }}
      />
    </>
  );

  // Welcome message — centered dialog, full dim (no spotlight)
  if (isOpen && currentStep && isBodyWelcomeStep) {
    return (
      <>
        <Backdrop open sx={{ zIndex: 1300, backgroundColor: "rgba(0, 0, 0, 0.7)" }} />
        {renderCenterDialog()}
      </>
    );
  }

  // Section step — spotlight target, dialog centered in viewport
  if (isOpen && currentStep && isCenterPopoverStep && targetRect) {
    return (
      <>
        {renderSpotlightBackdrop()}
        {renderCenterDialog({ hideBackdrop: true })}
      </>
    );
  }

  if (isOpen && currentStep && isCenterPopoverStep) {
    return null;
  }

  // Regular step with target element
  if (!isOpen || !currentStep || !targetRect || !targetElement) return null;

  return (
    <>
      {renderSpotlightBackdrop()}

      {/* Tooltip Popper - Only render if targetElement exists */}
      {targetElement && (
        <Popper
          ref={popperRef}
          open={!!targetElement}
          anchorEl={targetElement}
          placement={currentStep.placement || "bottom"}
          sx={{ zIndex: 1400 }}
          modifiers={[
            {
              name: "offset",
              options: {
                offset: [0, 12],
              },
            },
            {
              name: "preventOverflow",
              options: {
                boundary: "viewport",
              },
            },
          ]}
        >
          <Paper sx={GUIDED_TOUR_POPOVER_PAPER_SX}>
            {renderTourStepContent(
              stepIndex === totalSteps - 1 ? "Finish" : "Next"
            )}
          </Paper>
        </Popper>
      )}
    </>
  );
}
